import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { pointNearestTime } from '../policies/trip-track-geojson.policy';
import type { TripStop } from '../policies/trip-stops.policy';
import type { TrackPoint } from '../trips.api';
import { useTripEvents } from './use-trip-events';

/** The query parameter the Events tab links with: `?event=<id>` opens the overview with the map on where the truck was at that event. */
export const TRIP_EVENT_PARAM = 'event';

type Focus = { lng: number; lat: number };

interface UseTripFocusArgs {
  tripId: string;
  points: TrackPoint[];
}

/**
 * What the map is centred on: a stop the person picked, or the event the Events tab sent them from (placed at the fix nearest the
 * event's device time, because events carry no coordinates of their own). The event is resolved once, when its event and the track
 * have both loaded, so later live fixes never pull the map back. Picking a stop makes a new object, so the map returns to it after
 * the person has panned away.
 */
export function useTripFocus({ tripId, points }: UseTripFocusArgs) {
  const [params] = useSearchParams();
  const eventId = params.get(TRIP_EVENT_PARAM);
  const { state } = useTripEvents(eventId ? tripId : null);
  const [focus, setFocus] = useState<Focus | null>(null);
  const pointsRef = useRef(points);
  const hasPoints = points.length > 0;

  useEffect(() => {
    pointsRef.current = points;
  });

  useEffect(() => {
    if (!eventId || state.status !== 'loaded' || !hasPoints) return;
    const event = state.events.find((entry) => entry.id === eventId);
    const fix = event ? pointNearestTime(pointsRef.current, event.deviceTime) : null;
    if (fix) setFocus({ lng: fix.lng, lat: fix.lat });
  }, [eventId, state, hasPoints]);

  const focusStop = useCallback((stop: TripStop) => {
    if (stop.lng !== null && stop.lat !== null) setFocus({ lng: stop.lng, lat: stop.lat });
  }, []);

  return { focus, focusStop };
}
